import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { 
  ArrowLeft, 
  Globe, 
  Loader2,
  Check,
  X
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLanguages = () => {
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    fetchLanguages();
  }, []);

  const fetchLanguages = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/admin/localization/languages`);
      if (response.data.success) {
        setLanguages(response.data.languages);
      }
    } catch (error) {
      console.error('Error fetching languages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleLanguage = async (langCode) => {
    if (langCode === 'en') {
      alert('Cannot disable English - it is the default language');
      return;
    }
    
    try {
      setSaving(langCode);
      const response = await axios.put(`${API_URL}/api/admin/localization/languages/${langCode}/toggle`);
      if (response.data.success) {
        fetchLanguages();
      }
    } catch (error) {
      console.error('Error toggling language:', error);
      alert(error.response?.data?.detail || 'Failed to toggle language');
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const enabledCount = languages.filter(l => l.enabled).length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin/localization">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Language Settings</h1>
                <p className="text-sm text-gray-500">Enable or disable languages for your website</p>
              </div>
            </div>
            <Badge variant="outline" className="text-sm">
              {enabledCount} of {languages.length} enabled
            </Badge>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Info Card */}
        <Card className="mb-6 bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Globe className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-900">About Language Settings</h4>
                <p className="text-sm text-blue-700 mt-1">
                  Enable or disable languages that appear in the language switcher on your website. 
                  English is always enabled as the default language and cannot be disabled.
                  Disabled languages will not appear in the language dropdown for users.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Languages List */}
        <Card>
          <CardHeader>
            <CardTitle>Available Languages</CardTitle>
            <CardDescription>
              Toggle languages on or off to control which options users can select
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {languages.map((language) => (
                <div 
                  key={language.code}
                  className={`flex items-center justify-between p-4 ${
                    !language.enabled ? 'bg-gray-50' : ''
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <span className="text-2xl">{language.flag}</span>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{language.name}</span>
                        <span className="text-gray-500">({language.native_name})</span>
                        {language.is_default && (
                          <Badge variant="secondary" className="text-xs">Default</Badge>
                        )}
                      </div>
                      <span className="text-sm text-gray-500">Code: {language.code}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    {language.enabled ? (
                      <Badge className="bg-green-100 text-green-700 border-green-200">
                        <Check className="h-3 w-3 mr-1" />
                        Enabled
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-gray-500">
                        <X className="h-3 w-3 mr-1" />
                        Disabled
                      </Badge>
                    )}
                    
                    <Switch
                      checked={language.enabled}
                      onCheckedChange={() => handleToggleLanguage(language.code)}
                      disabled={saving === language.code || language.is_default}
                    />
                    
                    {saving === language.code && (
                      <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Note about changes */}
        <p className="text-sm text-gray-500 mt-4 text-center">
          Changes take effect immediately. Users will see the updated language options on their next page load.
        </p>
      </div>
    </div>
  );
};

export default AdminLanguages;
